import type { Question } from '../types/question';
import { isAxiosError } from 'axios';

export type ApiErrorBody = {
  error: string;
  message: string;
  header?: string;
  question?: Question;
};

export class ApiError extends Error {
  readonly code: string;
  readonly header?: string;
  readonly status?: number;
  readonly question?: Question;

  constructor(body: ApiErrorBody, status?: number) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.error;
    this.header = body.header;
    this.status = status;
    this.question = body.question;
  }
}

function isApiErrorBody(data: unknown): data is ApiErrorBody {
  return (
    data != null &&
    typeof data === 'object' &&
    'error' in data &&
    'message' in data &&
    typeof (data as ApiErrorBody).error === 'string' &&
    typeof (data as ApiErrorBody).message === 'string'
  );
}

export function parseApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (isAxiosError(error)) {
    const body = error.response?.data;
    if (isApiErrorBody(body)) {
      return new ApiError(
        {
          error: body.error,
          message: body.message,
          header: body.header,
          question: body.question,
        },
        error.response?.status,
      );
    }
    if (error.response?.status) {
      return new ApiError({ error: 'http_error', message: error.message }, error.response.status);
    }
  }

  if (error instanceof Error) {
    return new ApiError({ error: 'unknown', message: error.message });
  }

  return new ApiError({ error: 'unknown', message: 'internal_error' });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
